import type { Organization } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { CopyButton } from '@repo/ui/components/copy-button'
import { PageLayout } from '@repo/ui/components/page-layout'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { UserAvatar } from '@repo/ui/components/user-avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { useLocaleFormat } from '@repo/ui/hooks/use-locale-format'
import { Spinner } from '@repo/ui/spinner'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { MoreHorizontal, ScrollText, SearchX, WifiOff } from 'lucide-react'
import { translateKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { OperatorBreadcrumb } from '@/routes/-components/operator-breadcrumb'
import { OrganizationStatusBadge } from '@/routes/(private)/organizations/-components/organization-status-badge'
import {
	isRetryableStatus,
	lifecycleCommand
} from '@/routes/(private)/organizations/-lib/organization-status'
import { DeactivateOrganizationAction } from '@/routes/(private)/organizations/$organizationId/-components/deactivate-organization-action'
import { ReactivateOrganizationAction } from '@/routes/(private)/organizations/$organizationId/-components/reactivate-organization-action'
import { RetryProvisioningAction } from '@/routes/(private)/organizations/$organizationId/-components/retry-provisioning-action'
import { organizationDetailQueryOptions } from '@/routes/(private)/organizations/$organizationId/-query/query-options'

export const Route = createFileRoute(
	'/(private)/organizations/$organizationId'
)({
	component: OrganizationShell,
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<Organization | null>(
				queryClient,
				makeQueryKeys.organizations.detail(params.organizationId),
				(organization) =>
					organization?.name ??
					// Outside React and outside a render, so the facade's
					// key-string form rather than the hook. Same singleton,
					// same current language.
					translateKey(
						'operator-organizations:detail.not_found_title'
					)
			)
	}
})

/**
 * One customer, and the two ways of looking at them.
 *
 * **Who this is** — the name, the status and the lifecycle commands — belongs
 * to neither tab, so it is drawn here, once, above both. The read is drawn
 * here too, with its three failure branches, so a customer that does not
 * exist is said once rather than by each tab in its own words.
 *
 * ## Why there are tabs at all
 *
 * Until OPSSTD-9931 this was one scrolling page, and the people of the
 * organization were a card at the bottom of it. That was the wrong shape
 * twice over: a table in a column sized for cards wraps on every row, and the
 * roster is the thing an operator opens a customer *for* — asking them to
 * scroll past the provisioning ladder to reach it put the rare reading above
 * the common one.
 *
 * So: **Overview** is the machinery — how far the creation got, why it
 * stopped, what it holds, and the destruction. **People** is everybody in the
 * organization, which is a full-width list with its own paging and its own
 * actions.
 *
 * ## What the tabs are, and what they are not
 *
 * Routes, not panels. Each is addressable, so an operator can keep a
 * customer's roster open in a tab and reload it, and the browser's back
 * button moves between them. `Tabs` from the design system draws the same
 * thing and would not survive a reload, which is why it is not used here.
 *
 * ## No content surface, on either tab
 *
 * There is no file, no folder name, no bucket, no prefix and no way to open
 * anything, and that is the contract's doing rather than this file's
 * restraint: administering organizations is not access to what is in them.
 */
function OrganizationShell() {
	const { t } = useTranslation()
	const params = Route.useParams()
	const organization = useQuery(
		organizationDetailQueryOptions(params.organizationId)
	)

	// A read that failed is the route error boundary's business. Only "this id
	// names nothing" reaches the branch below, because the factory turns the
	// endpoint's `404` into `null` — so an unreachable server is never
	// reported to the operator as a destroyed customer.
	if (organization.error) {
		throw organization.error
	}

	// Offline before loading: with `networkMode: 'online'` a query the browser
	// cannot send is pending and *not* fetching, so a spinner gated on the
	// pending flag alone would turn forever without a request behind it.
	if (organization.isPaused) {
		return (
			<OrganizationNotice
				description={t(
					'operator-organizations:detail.offline_description'
				)}
				icon={<WifiOff />}
				title={t('operator-organizations:detail.offline_title')}
			/>
		)
	}

	if (organization.isPending) {
		return (
			<OrganizationNotice
				icon={<Spinner />}
				title={t('operator-organizations:detail.loading')}
			/>
		)
	}

	if (organization.data === null) {
		return (
			<OrganizationNotice
				description={t(
					'operator-organizations:detail.not_found_description'
				)}
				icon={<SearchX />}
				title={t('operator-organizations:detail.not_found_title')}
			/>
		)
	}

	return (
		<PageLayout breadcrumb={<OperatorBreadcrumb />}>
			<OrganizationIdentity organization={organization.data} />
			<Outlet />
		</PageLayout>
	)
}

/** The three readings that replace the whole page, in one shape. */
function OrganizationNotice(props: {
	icon: React.ReactNode
	title: string
	description?: string
}) {
	return (
		<PageLayout breadcrumb={<OperatorBreadcrumb />}>
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>{props.icon}</EmptyMedia>
					<EmptyTitle>{props.title}</EmptyTitle>
					{props.description !== undefined && (
						<EmptyDescription>{props.description}</EmptyDescription>
					)}
				</EmptyHeader>
			</Empty>
		</PageLayout>
	)
}

/**
 * Who this is and what can be done to it: the lifecycle commands sit here with
 * the name they act on, and the audit link, which is a way out rather than a
 * command, sits behind the overflow menu.
 *
 * **Under `PageLayout`'s header, not instead of it.** It was written as a
 * replacement first, and that took the breadcrumb and the sidebar trigger off
 * both tabs: `PageLayout` draws the one header row every page in both apps
 * shares, and a page that rolls its own loses the two controls living in it.
 * This is the second row — the customer's identity and the tabs — and it sits
 * inside the layout's content slot.
 */
function OrganizationIdentity({
	organization
}: {
	organization: Organization
}) {
	const { t } = useTranslation()
	const { formatDate } = useLocaleFormat()

	return (
		<header className='flex shrink-0 flex-col gap-4 border-b px-6 pt-4'>
			<div className='flex flex-wrap items-center justify-between gap-4'>
				<div className='flex min-w-0 items-center gap-3'>
					<UserAvatar
						className='size-11 rounded-lg text-base'
						name={organization.name}
					/>
					<div className='flex min-w-0 flex-col gap-1'>
						<div className='flex flex-wrap items-center gap-2'>
							{/*
							 * Not an `h1`. `QueryBreadcrumbs` makes the
							 * current step the page's heading, and this
							 * repeats that step — two `h1`s on one page is
							 * what the accessibility harness fails on, and
							 * the second one tells a screen reader nothing
							 * the first did not.
							 */}
							<p className='truncate font-semibold text-xl tracking-tight'>
								{organization.name}
							</p>
							<OrganizationStatusBadge
								status={organization.status}
							/>
						</div>
						<div className='flex flex-wrap items-center gap-x-1.5 text-muted-foreground text-xs'>
							<span>{organization.slug}</span>
							<span aria-hidden>·</span>
							<span className='inline-flex items-center gap-0.5'>
								<span className='font-mono'>
									{shortId(organization.id)}
								</span>
								<CopyButton
									size='icon-xs'
									value={organization.id}
								/>
							</span>
							<span aria-hidden>·</span>
							<span>
								{t('operator-organizations:detail.created_on', {
									date: formatDate(
										organization.createdAt,
										'P'
									)
								})}
							</span>
						</div>
					</div>
				</div>

				<div className='flex flex-wrap items-center gap-2'>
					{isRetryableStatus(organization.status) && (
						<RetryProvisioningAction organization={organization} />
					)}
					{lifecycleCommand(organization.status) === 'deactivate' && (
						<DeactivateOrganizationAction
							organization={organization}
						/>
					)}
					{lifecycleCommand(organization.status) === 'reactivate' && (
						<ReactivateOrganizationAction
							organization={organization}
						/>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger
							render={
								<Button size='icon' variant='outline'>
									<MoreHorizontal />
									<span className='sr-only'>
										{t(
											'operator-organizations:detail.more_actions'
										)}
									</span>
								</Button>
							}
						/>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem
								render={
									<Link
										search={{
											organizationId: organization.id
										}}
										to='/audit'
									/>
								}
							>
								<ScrollText />
								{t('operator-organizations:detail.view_audit')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			<OrganizationTabs organizationId={organization.id} />
		</header>
	)
}

/**
 * The two readings, as links rather than as a tab widget.
 *
 * `role='tablist'` with links inside would be a lie to a screen reader — these
 * navigate, they do not switch panels — so this is an ordinary navigation
 * landmark and the current page is marked with `aria-current`, which is what
 * `activeProps` sets. The underline is drawn on the link itself rather than
 * on a sliding indicator, so it survives a page that renders before the
 * router has settled.
 */
function OrganizationTabs({ organizationId }: { organizationId: string }) {
	const { t } = useTranslation()

	return (
		<nav
			aria-label={t('operator-organizations:detail.sections')}
			className='-mb-px flex items-center gap-1'
		>
			<OrganizationTab
				label={t('operator-organizations:detail.tab_overview')}
				organizationId={organizationId}
				to='/organizations/$organizationId'
			/>
			<OrganizationTab
				label={t('operator-organizations:people.title')}
				organizationId={organizationId}
				to='/organizations/$organizationId/people'
			/>
		</nav>
	)
}

function OrganizationTab(props: {
	label: string
	organizationId: string
	to:
		| '/organizations/$organizationId'
		| '/organizations/$organizationId/people'
}) {
	return (
		<Link
			activeOptions={{ exact: true }}
			/*
			 * The two states are written out in full, in `activeProps` and
			 * `inactiveProps`, and never layered on top of a shared base.
			 * Putting `border-transparent` in the common class and
			 * `border-primary` in `activeProps` leaves both on the element,
			 * and which one wins is decided by their order in the generated
			 * stylesheet rather than by the order they are written here. The
			 * current tab came out looking exactly like the other one, and
			 * the only thing marking it was the browser's focus ring.
			 */
			activeProps={{
				'aria-current': 'page',
				className: 'border-primary font-semibold text-foreground'
			}}
			className='border-b-2 px-3 pb-2.5 text-sm transition-colors'
			inactiveProps={{
				className:
					'border-transparent font-medium text-muted-foreground hover:border-border hover:text-foreground'
			}}
			params={{ organizationId: props.organizationId }}
			to={props.to}
		>
			{props.label}
		</Link>
	)
}

/**
 * The prefixed id cut to its prefix and last six characters — for
 * recognising; the copy button beside it carries the whole value for quoting.
 */
function shortId(id: string) {
	const separator = id.lastIndexOf('_')
	return `${id.slice(0, separator + 1)}${id.slice(-6).toLowerCase()}`
}
